import type { TFunction } from 'i18next';
import type { IconType } from 'react-icons';
import {
  BiSolidGraduation,
  BiUserPlus,
  BiTime,
  BiSolidFileBlank,
  BiListCheck,
} from 'react-icons/bi';
import { formatDateOnly } from '@/lib/format';
import type {
  DashboardActivityEntityType,
  DashboardCardKey,
  DashboardSummaryPeriod,
  ResolvedPeriod,
} from '@/types/shared';

/** One `--color-accent-*`/`--color-accent-*-tint` token pair (`tokens.css`, T88 §1.1) per stat
 * card — drives the icon-chip background/glyph color in `StatCardWidget` (T91 §1.1/§1.3). */
export type DashboardAccent = 'blue' | 'purple' | 'orange' | 'teal' | 'green';

/**
 * Frontend-owned presentation registry for the Admin stat-card widgets (T81: "the backend emits
 * each card's `value` and `deepLink`; the frontend owns titles/icons/labels"). Order here is the
 * fixed DOM/tab-order (`11-dashboard-widget-grid-ux.md §1.1`) — never reordered via CSS `order`.
 * Icons are decorative-only (`aria-hidden`, per §1.1's icon-precedence note — glyph contributes
 * nothing to the card's accessible name; only label -> number -> "view all" text do), each paired
 * with a fixed accent color per `13-dashboard-restyle-spec.md` §1.1 (chosen so no two adjacent
 * cards share a color).
 */
export interface StatCardWidgetDef {
  key: DashboardCardKey;
  icon: IconType;
  accent: DashboardAccent;
  /** i18n key -> localized widget title (also its `<h3>` text and part of the card's accessible
   * name via natural concatenation, §2.2). For a `scoped` card this is ONLY used in the default
   * (no period selected) state — see `getScopedCardTitle`. */
  titleKey: string;
  /** i18n key -> localized "View all" trailing affordance text. */
  viewAllKey: string;
  /**
   * T97/T100 — `true` for the 2 cards the period filter actually scopes (`newLeadsToday`,
   * `examsBookedThisWeek`). The other 3 "live" cards ignore the filter entirely and instead carry
   * a persistent "as of now" marker (`StatCardWidget`) in every state.
   */
  scoped?: boolean;
  /** Required when `scoped` — i18n key -> the period-neutral base label used to build the
   * filtered title, e.g. "New leads" (→ "New leads — This month"). */
  baseLabelKey?: string;
}

export const STAT_CARD_WIDGETS: StatCardWidgetDef[] = [
  {
    key: 'totalStudents',
    icon: BiSolidGraduation,
    accent: 'blue',
    titleKey: 'dashboard.widgets.totalStudents.title',
    viewAllKey: 'dashboard.widgets.totalStudents.viewAllLabel',
  },
  {
    key: 'newLeadsToday',
    icon: BiUserPlus,
    accent: 'purple',
    titleKey: 'dashboard.widgets.newLeadsToday.title',
    viewAllKey: 'dashboard.widgets.newLeadsToday.viewAllLabel',
    scoped: true,
    baseLabelKey: 'dashboard.widgets.newLeadsToday.baseLabel',
  },
  {
    key: 'pendingAccounts',
    icon: BiTime,
    accent: 'orange',
    // i18n key group is `overdueAccounts` (matches the UX doc's naming for widget 3) even though
    // the T81 DTO key is `pendingAccounts` — the DTO key is just the map lookup, not the copy.
    titleKey: 'dashboard.widgets.overdueAccounts.title',
    viewAllKey: 'dashboard.widgets.overdueAccounts.viewAllLabel',
  },
  {
    key: 'documentsIncomplete',
    icon: BiSolidFileBlank,
    accent: 'teal',
    titleKey: 'dashboard.widgets.incompleteDocuments.title',
    viewAllKey: 'dashboard.widgets.incompleteDocuments.viewAllLabel',
  },
  {
    key: 'examsBookedThisWeek',
    icon: BiListCheck,
    accent: 'green',
    titleKey: 'dashboard.widgets.examsBookedThisWeek.title',
    viewAllKey: 'dashboard.widgets.examsBookedThisWeek.viewAllLabel',
    scoped: true,
    baseLabelKey: 'dashboard.widgets.examsBookedThisWeek.baseLabel',
  },
];

/** `ResolvedPeriod.period` -> the i18n key for its non-custom context suffix ("Today" / "This
 * month" / "This year"). `custom` is handled separately in `getScopedCardTitle` since it needs
 * the actual `from`/`to` dates interpolated, not a fixed label. */
const PERIOD_CONTEXT_KEY: Partial<Record<DashboardSummaryPeriod, string>> = {
  today: 'dashboard.widgets.common.periodContext.today',
  'this-month': 'dashboard.widgets.common.periodContext.thisMonth',
  'this-year': 'dashboard.widgets.common.periodContext.thisYear',
};

/**
 * T97 §"Scoped card titles": resolves a `scoped` stat card's title.
 * - No `resolvedPeriod` (default, unfiltered view) → the card's own unchanged default string
 *   (`widget.titleKey`, e.g. "New leads today").
 * - `resolvedPeriod` present → period-neutral base + context, e.g. "New leads — This month", or
 *   "New leads — 2026-07-01 – 2026-07-31" for `custom` (dates formatted, never recomputed —
 *   `resolvedPeriod.from/to` come straight from the server echo, T98).
 */
export function getScopedCardTitle(
  widget: StatCardWidgetDef,
  resolvedPeriod: ResolvedPeriod | null,
  t: TFunction,
  language: string,
): string {
  if (!resolvedPeriod || !widget.baseLabelKey) {
    return t(widget.titleKey);
  }

  const base = t(widget.baseLabelKey);
  const context =
    resolvedPeriod.period === 'custom'
      ? // Wrap the LTR date range in isolate chars (U+2066 … U+2069) so "from – to" renders as one
        // left-to-right run and never visually reorders to "to – from" inside the Arabic RTL title.
        `⁦${t('dashboard.widgets.common.periodContext.custom', {
          from: formatDateOnly(resolvedPeriod.from, language),
          to: formatDateOnly(resolvedPeriod.to, language),
        })}⁩`
      : t(PERIOD_CONTEXT_KEY[resolvedPeriod.period] ?? widget.titleKey);

  return t('dashboard.widgets.common.scopedTitleTemplate', { base, context });
}

/**
 * Activity-feed entity-label i18n templates (`11-dashboard-widget-grid-ux.md §3.2`/§4.3). Each
 * template contains `{{name}}` and (where applicable) `{{studentCode}}` placeholders — rendered
 * via `renderEntityLabel` in `ActivityFeedWidget` so the name/code substrings can be wrapped in
 * `<bdi>` individually rather than baked into one plain-string interpolation (§5 "Numerals & bidi").
 * Note: the UX doc's original draft used separate `{{nameAr}}`/`{{nameEn}}` placeholders, but the
 * frozen T81 `DashboardActivityItem` DTO only carries a single resolved `entityLabel` string (the
 * student's Arabic name for student-scoped entities, or the lead's `fullName`) — templates below
 * use `{{name}}` to match the contract actually being built against.
 */
export const ACTIVITY_ENTITY_LABEL_KEY: Record<DashboardActivityEntityType, string> = {
  student: 'dashboard.widgets.activityFeed.entityLabel.student',
  lead: 'dashboard.widgets.activityFeed.entityLabel.lead',
  studentAffairs: 'dashboard.widgets.activityFeed.entityLabel.studentAffairs',
  // T81's `paymentPlan` entity type covers both payment_plans and installments edits (rolled up
  // to the plan) — the UX doc calls this "Accounts / payment plan", i18n key group `accounts`.
  paymentPlan: 'dashboard.widgets.activityFeed.entityLabel.accounts',
  examBooking: 'dashboard.widgets.activityFeed.entityLabel.examBooking',
};
